import styled from "styled-components";
import { motion } from "framer-motion";
import { Code, Brain, CheckCircle, ArrowRight } from "lucide-react";

/* ================= PAGE ================= */

const Page = styled.section`
  padding: 200px 20px;
  background: ${({ theme }) => theme.colors.bg};
`;

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

/* ================= TEXT ================= */

const Heading = styled(motion.h1)`
  text-align: center;
  font-size: clamp(40px, 5vw, 58px);
  font-weight: 900;
`;

const Sub = styled(motion.p)`
  margin: 24px auto 0;
  max-width: 900px;
  text-align: center;
  font-size: 18px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Highlight = styled.span`
  color: #60a5fa;
  font-weight: 800;
`;

/* ================= QUESTION BLOCK ================= */

const QuestionBox = styled(motion.div)`
  margin: 120px auto 0;
  padding: 60px;
  border-radius: 36px;
  text-align: center;
  background: linear-gradient(
    180deg,
    rgba(59, 130, 246, 0.12),
    rgba(59, 130, 246, 0.02)
  );
  border: 1px solid rgba(59, 130, 246, 0.25);
`;

const Question = styled.div`
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 900;
`;

/* ================= GRID ================= */

const Grid = styled.div`
  margin-top: 140px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 48px;
  border-radius: 32px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.04),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Icon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
  margin-bottom: 22px;
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 16px;
`;

const List = styled.ul`
  padding-left: 18px;

  li {
    margin-bottom: 10px;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/* ================= CTA ================= */

const CTA = styled.button`
  margin: 120px auto 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 26px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 900;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
`;

/* ================= ANIMATION ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

/* ================= COMPONENT ================= */

export default function MAANGPrepIntroPage() {
  return (
    <Page>
      <Container>
        <Heading variants={fadeUp} initial="hidden" animate="show">
          MAANG Interviews: <Highlight>Language Confusion</Highlight> First
        </Heading>

        <Sub variants={fadeUp} initial="hidden" animate="show">
          The most common question after choosing MAANG preparation:
          <br />
          <b>“DSA kis language me karni chahiye?”</b>
        </Sub>

        {/* BIG QUESTION */}
        <QuestionBox variants={fadeUp} initial="hidden" animate="show">
          <Question>
            JavaScript, Java, Python or C++ —
            <br />
            <Highlight>Does MAANG care?</Highlight>
          </Question>
        </QuestionBox>

        {/* ANSWER */}
        <Grid>
          <Card whileHover={{ y: -10 }}>
            <Icon>
              <Brain size={30} />
            </Icon>

            <Title>Short Answer</Title>

            <List>
              <li>
                <b>MAANG does NOT hire based on language</b>
              </li>
              <li>They evaluate how you think & solve problems</li>
              <li>DSA concepts are language-agnostic</li>
              <li>Clean logic & explanation matter more</li>
              <li>A strong solution in Python = strong solution in C++</li>
            </List>
          </Card>

          <Card whileHover={{ y: -10 }}>
            <Icon>
              <Code size={30} />
            </Icon>

            <Title>Language Guidance (Reality)</Title>

            <List>
              <li>
                <b>C++</b> → Popular in competitive programming
              </li>
              <li>
                <b>Java</b> → Very common in enterprise & interviews
              </li>
              <li>
                <b>Python</b> → Fast to write, great for interviews
              </li>
              <li>
                <b>JavaScript</b> → Accepted, but needs discipline
              </li>
              <li>Choose the language you can think clearly in</li>
            </List>
          </Card>
        </Grid>

        {/* WHAT MAANG ACTUALLY CHECKS */}
        <Grid>
          <Card whileHover={{ y: -10 }}>
            <Icon>
              <CheckCircle size={30} />
            </Icon>

            <Title>What MAANG Actually Evaluates</Title>

            <List>
              <li>Problem-solving approach</li>
              <li>Time & space complexity clarity</li>
              <li>Edge case handling</li>
              <li>Code readability</li>
              <li>Communication & reasoning</li>
            </List>
          </Card>

          <Card whileHover={{ y: -10 }}>
            <Icon>
              <CheckCircle size={30} />
            </Icon>

            <Title>
              What MAANG Does <Highlight>Not Hire You For</Highlight>
            </Title>
            <List>
              <li>
                Competitive programming <b>ranks alone</b> without clear
                explanation
              </li>
              <li>
                Writing clever code that you <b>cannot explain</b>
              </li>
              <li>
                Using STL or library shortcuts <b>without understanding</b>
              </li>
              <li>Memorized solutions without reasoning</li>
              <li>Language-specific tricks instead of core logic</li>
            </List>
          </Card>
        </Grid>
        {/* <Sub style={{ marginTop: 60, maxWidth: 900, opacity: 0.9 }}>
          💡 <Highlight>Important clarity:</Highlight>
          Competitive programming and LeetCode practice are{" "}
          <b>extremely useful</b> — they sharpen problem-solving speed and
          pattern recognition.
          <br />
          <br />
          However, MAANG interviews focus on
          <Highlight>
            {" "}
            how you arrive at the solution, explain trade-offs, and adapt
          </Highlight>
          , not just how fast you code.
        </Sub> */}
        {/* CTA */}
        <CTA>
          Show me the MAANG preparation roadmap <ArrowRight size={18} />
        </CTA>
      </Container>
    </Page>
  );
}
